import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import config from '../config/index.json';
import { Divider } from './Divider';

const SheetMetalCarousel = () => {
  const { sheetMetalProducts } = config;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Auto-play functionality
  useEffect(() => {
    if (!isPaused && sheetMetalProducts?.items?.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) =>
          (prevIndex + 1) % sheetMetalProducts.items.length
        );
      }, 4000); // Change slide every 4 seconds

      return () => clearInterval(interval);
    }
  }, [isPaused, sheetMetalProducts?.items?.length]);

  if (!sheetMetalProducts?.items?.length) {
    return null;
  }

  const { title, subtitle, items } = sheetMetalProducts;

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? items.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) =>
      (prevIndex + 1) % items.length
    );
  };

  const getPrevIndex = () => {
    return currentIndex === 0 ? items.length - 1 : currentIndex - 1;
  };

  const getNextIndex = () => {
    return (currentIndex + 1) % items.length;
  };

  return (
    <section className="py-12" id="sheet-metal-products">
      <div className="container max-w-7xl mx-auto px-4">
        {/* Header */}
        <h2 className="w-full my-2 text-5xl font-bold leading-tight text-center text-primary">
          {title}
        </h2>
        {subtitle && (
          <p className="text-center text-xl text-gray-600 mt-2 mb-6">
            {subtitle}
          </p>
        )}
        <Divider />

        {/* Carousel Container */}
        <div
          className="relative mt-12 mb-8"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Three Image Display - Previous, Current, Next */}
          <div className="relative h-[500px] md:h-[600px] flex items-center justify-center gap-4 overflow-visible">
            {/* Previous Image (Left Preview) */}
            <motion.div
              key={`prev-${getPrevIndex()}`}
              className="hidden md:flex flex-col items-center justify-center w-1/4 h-[70%] cursor-pointer opacity-60 hover:opacity-80 transition-opacity"
              onClick={goToPrevious}
              whileHover={{ scale: 1.05 }}
            >
              <div className="relative w-full h-full flex items-center justify-center bg-white rounded-xl shadow-lg p-4">
                <img
                  src={items[getPrevIndex()].img}
                  alt={items[getPrevIndex()].name}
                  className="max-h-full max-w-full object-contain rounded-lg"
                />
              </div>
              <p className="mt-3 text-sm text-gray-500 text-center font-medium">
                {items[getPrevIndex()].name}
              </p>
            </motion.div>

            {/* Current Image (Center - Main Display) */}
            <div className="w-full md:w-1/2 h-full">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.5 }}
                  className="h-full flex flex-col items-center justify-center"
                >
                  <div className="relative w-full h-full flex items-center justify-center bg-white rounded-2xl shadow-2xl p-8">
                    <img
                      src={items[currentIndex].img}
                      alt={items[currentIndex].name}
                      className="max-h-full max-w-full object-contain rounded-lg"
                    />
                  </div>

                  {/* Product Name Caption */}
                  <div className="mt-6 text-center">
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-800">
                      {items[currentIndex].name}
                    </h3>
                    {items[currentIndex].description && (
                      <p className="text-gray-600 mt-2 max-w-2xl">
                        {items[currentIndex].description}
                      </p>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Next Image (Right Preview) */}
            <motion.div
              key={`next-${getNextIndex()}`}
              className="hidden md:flex flex-col items-center justify-center w-1/4 h-[70%] cursor-pointer opacity-60 hover:opacity-80 transition-opacity"
              onClick={goToNext}
              whileHover={{ scale: 1.05 }}
            >
              <div className="relative w-full h-full flex items-center justify-center bg-white rounded-xl shadow-lg p-4">
                <img
                  src={items[getNextIndex()].img}
                  alt={items[getNextIndex()].name}
                  className="max-h-full max-w-full object-contain rounded-lg"
                />
              </div>
              <p className="mt-3 text-sm text-gray-500 text-center font-medium">
                {items[getNextIndex()].name}
              </p>
            </motion.div>
          </div>

          {/* Previous Button */}
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-10 group"
            aria-label="Previous slide"
          >
            <svg
              className="w-6 h-6 text-primary group-hover:text-secondary transition-colors"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Next Button */}
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 z-10 group"
            aria-label="Next slide"
          >
            <svg
              className="w-6 h-6 text-primary group-hover:text-secondary transition-colors"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Indicator Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {items.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-full ${index === currentIndex
                    ? 'w-8 h-3 bg-primary'
                    : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                  }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Pause Indicator (shown when hovering) */}
          {isPaused && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm z-10"
            >
              Paused
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export { SheetMetalCarousel };
